<template>
  <BittsLoading :is-loading="isInitialLoading">
    <div
      class="credit-usage-user-table"
      :style="minHeightStyle"
      data-testid="credit-usage-user-table"
    >
      <BittsEmptyState
        v-if="!rows.length"
        :font-awesome-icon="['far', 'users']"
        :font-awesome-icon-style="{ height: '26px', width: '26px' }"
        font-awesome-icon-class="empty-state-icon"
        title="No user usage yet"
        description="Users will appear here once they start using credits."
      />
      <BittsTablePro
        v-else
        :columns="columns"
        :rows="rows"
        :row-height="ROW_HEIGHT"
        :pagination="isPaginated"
        :pagination-page-size="PAGE_SIZE"
        :suppress-movable-columns="true"
        :always-show-horizontal-scroll="false"
        :use-search="true"
        :search-query="searchQuery"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsEmptyState, BittsLoading, BittsTablePro } from '@crossbeam/bitts';

import { ColDef } from '@ag-grid-community/core';
import { computed } from 'vue';

import {
  CreditUsageResponse,
  CreditUsageUser,
} from '@/composables/credits/useCreditUsage';
import { formatNumberWithCommas } from '@/utils';

import { formatCreditActivity } from './creditUsageFormatters';
import CreditUsageNameCell from './CreditUsageNameCell.vue';

const { isLoading, data } = defineProps<{
  data: CreditUsageResponse | null;
  isLoading: boolean;
  searchQuery?: string;
}>();

const ROW_HEIGHT = 44;
const PAGE_SIZE = 10;
const HEADER_HEIGHT = 40;
const PAGINATION_HEIGHT = 52;

const isInitialLoading = computed(() => isLoading && !data);

const rows = computed<CreditUsageUser[]>(() =>
  (data?.users ?? []).filter((user) => (user.credits_used ?? 0) > 0),
);

const isPaginated = computed(() => rows.value.length > PAGE_SIZE);

// Reserve height for the rows that render on a page (capped at the page size)
// so filtering the list down via search doesn't shrink the modal.
const minHeightStyle = computed(() => {
  if (!rows.value.length) return undefined;
  const reservedRows = Math.min(rows.value.length, PAGE_SIZE);
  const paginationHeight = isPaginated.value ? PAGINATION_HEIGHT : 0;
  const minHeight =
    reservedRows * ROW_HEIGHT + HEADER_HEIGHT + paginationHeight;
  return { minHeight: `${minHeight}px` };
});

const columns: ColDef<CreditUsageUser>[] = [
  {
    headerName: 'Name',
    field: 'name',
    cellRenderer: CreditUsageNameCell,
    cellRendererParams: { kind: 'user' },
    flex: 2,
    minWidth: 200,
    sortable: true,
    resizable: false,
    getQuickFilterText: (params) =>
      `${params.data?.name ?? ''} ${params.data?.email ?? ''}`,
  },
  {
    headerName: 'Credits used',
    field: 'credits_used',
    flex: 1,
    minWidth: 140,
    sortable: true,
    resizable: false,
    valueFormatter: (params) => formatNumberWithCommas(params.value ?? 0),
  },
  {
    headerName: 'Last activity',
    field: 'last_activity',
    flex: 1,
    minWidth: 200,
    sortable: true,
    resizable: false,
    sort: 'desc',
    valueFormatter: (params) => formatCreditActivity(params.value),
    getQuickFilterText: (params) => formatCreditActivity(params.value),
  },
];
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
:deep() .empty-state-icon {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
</style>
